// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.
import {TranslateModule, TranslateLoader, TranslateService} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {BrowserModule} from '@angular/platform-browser';
import { ErrorHandler, NgModule, inject, provideAppInitializer } from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {AppRoutingModule, routingComponents} from './app-routing.module';
import {AppComponent} from './app.component';
import {CommonModule} from '@angular/common';

import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {MatBottomSheetModule, MAT_BOTTOM_SHEET_DEFAULT_OPTIONS} from '@angular/material/bottom-sheet';
import {MatCheckboxModule} from '@angular/material/checkbox';
import { MatLineModule, MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import {MatButtonModule} from '@angular/material/button';
import {MatInputModule} from '@angular/material/input';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatRadioModule} from '@angular/material/radio';
import {MatSelectModule} from '@angular/material/select';
import {MatSliderModule} from '@angular/material/slider';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatMenuModule} from '@angular/material/menu';
import {MatSidenavModule} from '@angular/material/sidenav';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatListModule} from '@angular/material/list';
import {MatGridListModule} from '@angular/material/grid-list';
import {MatCardModule} from '@angular/material/card';
import {MatStepperModule} from '@angular/material/stepper';
import {MatTabsModule} from '@angular/material/tabs';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import {MatChipsModule} from '@angular/material/chips';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatDialogModule} from '@angular/material/dialog';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {MatTableModule} from '@angular/material/table';
import {MatSortModule} from '@angular/material/sort';
import {MatPaginatorModule} from '@angular/material/paginator';
import {LayoutModule} from '@angular/cdk/layout';
import {AuthService} from 'src/services/auth-service';
import {UserService} from 'src/services/user-service';
import {D3eService} from 'src/services/d3e-service';
import {NotificationService} from 'src/services/notification-service';
import {HttpsRequestInterceptor} from 'src/interceptors/https-interceptor';
import {ErrorInterceptor} from 'src/interceptors/error-interceptor';
import {ActionService} from 'src/services/action-service';

import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {JwtModule, JwtHelperService} from '@auth0/angular-jwt';
import {EditSubscriptionNameComponent} from './dialogs/edit-subscription-name/edit-sub-name.component';
import {GoogleAuthDialogComponent} from './pages/profile/twofactor.component';
import {AutofocusDirective} from './shared/directives/autofocus.directive';
import {ResetPasswordComponent} from './pages/reset-password/reset-password.component';
import {NumericInputComponent} from './shared/components/numeric-input/numeric-input.component';
import {GlobalD3ePolicyComponent} from './pages/global-d3e-policy/global-d3e-policy.component';
import {D3eDeviceComponent} from './pages/d3e-device/d3e-device.component';
import {NgxFilesizeModule} from 'ngx-filesize';
import {IntegrationsComponent} from './pages/integrations/integrations.component';
import {CarbonBlackComponent} from './pages/integrations/carbon-black/carbon-black.component';
import {CyberarkComponent} from './pages/integrations/cyberark/cyberark.component';
import {HistoryComponent} from './pages/history/history.component';
import {ThirdPartyComponent} from './pages/integrations/third-party/third-party.component';
import {DefaultFileFormatComponent} from './shared/components/default-file-format/default-file-format.component';
import {ClientPolicyService} from 'src/services/client-policy.service';
import {CustomFileFormatComponent} from './shared/components/custom-file-format/custom-file-format.component';
import {PolicyFoldersComponent} from './shared/components/policy-folders/policy-folders.component';
import {PolicyEditorComponent} from './shared/components/policy-editor/policy-editor.component';
import {PolicySettingsComponent} from './shared/components/policy-settings/policy-settings.component';
import {PolicyListComponent} from './pages/global-d3e-policy/policy-list/policy-list.component';
import {DragDropModule} from '@angular/cdk/drag-drop';
import {ActiveLockConfigurationComponent} from './pages/d3e-device/active-lock-configuration/active-lock-configuration.component';
import {PageDeactivateService} from 'src/services/page-deactivate.service';
import {ProtectionLevelBottomSheetComponent} from './shared/components/default-file-format/protection-level-bottom-sheet/protetction-level-bottom-sheet.component';
import {SophosComponent} from './pages/integrations/sophos/sophos.component';
import {DebounceClickDirective} from './shared/directives/debounce-click.directive';
import {ThrottleClickDirective} from './shared/directives/throttle-click.directive';
import {DebounceInputDirective} from './shared/directives/debounce-input.directive';
import {TopNDevicesComponent} from './pages/dashboard/top.n.devices/top.n.devices.component';
import {TopNThreatsComponent} from './pages/dashboard/top.n.threats/top.n.threats.component';
import {DeviceOverviewComponent} from './pages/dashboard/device.overview/device.overview.component';
import {DeviceConfigComponent} from './pages/dashboard/device.config/device.config.component';
import {ThreatActivityComponent} from './pages/dashboard/threat.activity/threat.activity.component';
import {NgxChartsModule} from '@swimlane/ngx-charts';
import {UserSettingsComponent} from './pages/profile/user-settings/user-settings.component';
import {CountryCodeComponent} from './shared/components/country-code/country-code.component';
import {PolicyKnownGoodComponent} from './shared/components/policy-known-good/policy-known-good.component';
import {PathListComponent} from './shared/components/policy-known-good/path-list/path-list.component';
import {UploadDialogComponent} from './shared/components/policy-known-good/upload-dialog/upload-dialog.component';
import {CiscoAmpComponent} from './pages/integrations/cisco-amp/cisco-amp.component';
import {CiscoDuoComponent} from './pages/integrations/cisco-duo/cisco-duo.component';
import {AdminComponent} from './pages/admin/admin.component';
import {UserGroupRoleComponent} from './dialogs/user-group-role/user-group-role.component';
import {EventViewerComponent} from './pages/event-viewer/event-viewer.component';
import {PolicySafeAppComponent} from './shared/components/policy-known-good/policy-safe-app/policy-safe-app.component';
import {AddOrganizationComponent} from './dialogs/add-organization/add-organization.component';
import {AddChildSubscriptionComponent} from './dialogs/add-child-subscription/add-child-subscription.component';
import {EditOrgComponent} from './dialogs/edit-org/edit-org.component';
import {EditLicenseCountComponent} from './dialogs/edit-license-count/edit-license-count.component';
import {EditExpDateComponent} from './dialogs/edit-exp-date/edit-exp-date.component';
import {EditUserDialogComponent} from './dialogs/edit-user/edit-user.component';
import {AlertDialogComponent} from './dialogs/alert/alert.component';
import {ActionConfirmationDialogComponent} from './shared/components/action-confirmation-dialog/action-confirmation-dialog.component';
import {IntegrationCardComponent} from './pages/integrations/integration-card/integration-card.component';
import {DataDeceptionFilesComponent} from './shared/components/data-deception-files/data-deception-files.component';
import {FileDecryptionComponent} from './pages/encryption/file-decryption.component';
import {DndDropFileDirective} from './shared/directives/dnd-drop-file.directive';
import {ProgressComponent} from './shared/components/progress/progress.component';
import {ManageKeysComponent} from './pages/manage-keys/manage-keys.component';
import {AngularDualListBoxModule} from 'angular-dual-listbox';
import {D3eDownloadComponent} from './pages/d3e-download/d3e-download.component';
import {InvitationComponent} from './pages/invitation/invitation.component';
import {ManageUsersComponent} from './pages/manage-users/manage-users.component';
import {AddPeerUserDialogComponent} from './pages/manage-users/add-peer-user-dialog/add-peer-user-dialog.component';
import {EditPeerUserDialogComponent} from './pages/manage-users/edit-peer-user-dialog/edit-peer-user-dialog.component';
import {ReplaceSpacePipe} from './shared/pipes/replaceSpace.pipe';
import {PasswordComponent} from './shared/components/password/password.component';
import {NewsComponent} from './dialogs/news/news.component';
import {ImportExtsFoldersComponent} from './dialogs/import-exts-folders/import-exts-folders.component';
import {LifecycleTrackerDirective} from './shared/directives/lifecycle-tracker.directive';
import {D3eVersionHistoryComponent} from './pages/d3e-version-history/d3e-version-history.component';
import {EndpointsComponent} from './pages/endpoints/endpoints.component';
import {AssignGroupDialogComponent} from './pages/endpoints/assign-group-dialog/assign-group-dialog.component';
import {ServiceWorkerModule} from '@angular/service-worker';
import {environment} from '../environments/environment';
import { GroupsComponent } from './pages/groups/groups.component';
import { AddGroupDialogComponent } from './pages/groups/add-group-dialog/add-group-dialog.component';
import { EditGroupDialogComponent } from './pages/groups/edit-group-dialog/edit-group-dialog.component';
import { RedirectorComponent } from './shared/components/redirector/redirector.component';
import { DownloadedFreeComponent } from './pages/downloaded-free/downloaded-free.component';
import { LogoCardPageComponent } from './shared/logo-card-page/logo-card-page.component';
import { SaveKeyDialogComponent } from './pages/manage-users/save-key-dialog/save-key-dialog.component';
import { SecureSsdComponent } from './pages/secure-ssd/secure-ssd.component';
import { ColumnChooserComponent } from './shared/components/column-chooser/column-chooser.component';
import { CustomSnackBarComponent } from './shared/components/custom-snack-bar/custom-snack-bar.component';
import { AdminSettingsComponent } from './pages/admin-settings/admin-settings.component';
import { FileDecryptComponent } from './pages/encryption/file-decrypt/file-decrypt.component';
import { DiskSizePipe } from './shared/pipes/disk-size.pipe';
import { D3eInvitationComponent } from './pages/d3e-invitation/d3e-invitation.component';
import { WelcomeComponent } from './pages/d3e-invitation/welcome/welcome.component';
import {ConfigurationService, configurationServiceFactory} from '../services/configuration-service';
import { HeaderInfoComponent } from './dialogs/header-info/header-info.component';
import { GetPasswordComponent } from './dialogs/get-password/get-password.component';
import { supportedLanguages } from '../types/language';
import { MultiLineOptionDirective } from './shared/directives/multi-line-option.directive';
import { LanguageSelectorComponent } from './shared/components/language-selector/language-selector.component';
import { ActiveDirectoryComponent } from './pages/integrations/active-directory/active-directory.component';
import { LoginMsComponent } from './pages/login-ms/login-ms.component';
import { VerifyEmailComponent } from './pages/verify-email/verify-email.component';
import { EmailVerifyComponent } from './shared/email-verify/email-verify.component';
import { ReparentDialogComponent } from './dialogs/reparent-dialog/reparent-dialog.component';
import { DownloadCigentComponent } from './shared/components/download-cigent/download-cigent.component';
import { PlusTrialComponent } from './pages/plus-trial/plus-trial.component';
import { PasswordRulesComponent } from './dialogs/password-rules/password-rules.component';
import { EditLicenseDurationComponent } from './shared/components/edit-license-duration/edit-license-duration.component';
import { AlertClipboardCopyComponent } from './dialogs/alert-clipboard-copy/alert-clipboard-copy.component';
import { SecurityApprovalBannerComponent } from './shared/components/security-approval-banner/security-approval-banner.component';
import { SecurityRequestStatusDialogComponent } from './pages/encryption/security-request-status-dialog/security-request-status-dialog.component';
import { RecoveryKeyRequestComponent } from './pages/encryption/recovery-key-request/recovery-key-request.component';
import { SecurityRequestDialogComponent } from './pages/encryption/security-request-dialog/security-request-dialog.component';
import { CreateAdminEventComponent } from './dialogs/create-admin-event/create-admin-event.component';
import { ButtonFocusRemoverDirective } from './shared/directives/button-focus-remover.directive';
import { CigentHeaderComponent } from './shared/components/cigent-header/cigent-header.component';
import { CigentCapabilitiesComponent } from './shared/components/cigent-capabilities/cigent-capabilities.component';
import { CigentFooterComponent } from './shared/components/cigent-footer/cigent-footer.component';
import { AzureActivateComponent } from './pages/azure-activate/azure-activate.component';
import { IndirectLoginSuccessComponent } from './pages/indirect-login-success/indirect-login-success.component';
import { ReParentUserDialogComponent } from './dialogs/move-user-dialog/re-parent-user-dialog.component';
import { ThemeService } from '../services/theme.service';
import { SentinelOneComponent } from './pages/integrations/sentinel-one/sentinel-one.component';
import { DeleteOrganizationDialogComponent } from './dialogs/delete-organization-dialog/delete-organization-dialog.component';
import { WINDOW_PROVIDERS } from './window.providers';
import { PriorityLockBannerComponent } from './shared/components/priority-lock-banner/priority-lock-banner.component';
import { EncryptionEventViewerComponent } from './pages/encryption-event-viewer/encryption-event-viewer.component';
import { ExpiresInPipe } from './shared/pipes/expires-in.pipe';
import { MaintenanceComponent } from './pages/maintenance/maintenance.component';
import { CryptographyComponent } from './pages/cryptography/cryptography.component';
import { UploadKeyDialogComponent } from './pages/manage-keys/upload-key-dialog/upload-key-dialog.component';
import { KeyStatusTextPipe } from './pages/manage-keys/key-status-text.pipe';
import { ExportKeyDialogComponent } from './pages/manage-keys/export-key-dialog/export-key-dialog.component';
import { ExportD3eKeysDialogComponent } from './pages/manage-keys/export-d3e-keys-dialog/export-d3e-keys-dialog.component';
import { EditKeyDialogComponent } from './pages/manage-keys/edit-key-dialog/edit-key-dialog.component';
import { PasswordAlertDialogComponentComponent } from './dialogs/password-alert-dialog-component/password-alert-dialog-component.component';
import { IntegrationTestingComponent } from './pages/integrations/integration-testing/integration-testing.component';
import { RansomwareFileTypesComponent } from './shared/components/ransomware-file-types/ransomware-file-types.component';
import { GroupDetailsComponent } from './pages/groups/group-details/group-details.component';
import { GroupDetailsDeviceComponent } from './pages/groups/group-details-device/group-details-device.component';
import { GroupDetailsUserComponent } from './pages/groups/group-details-user/group-details-user.component';
import { OrgsOverviewComponent } from './pages/admin/orgs-overview/orgs-overview.component';
import { OrgsOverviewOrgComponent } from './pages/admin/orgs-overview/orgs-overview-org/orgs-overview-org.component';
import { OrgsOverviewUserComponent } from './pages/admin/orgs-overview/orgs-overview-user/orgs-overview-user.component';
import { LockEndpointsDialogComponent } from './pages/endpoints/lock-endpoints-dialog/lock-endpoints-dialog.component';
import {StoreModule} from '@ngrx/store';
import {EffectsModule} from '@ngrx/effects';
import {UserEffects} from './store/user/user.effects';
import {userReducer} from './store/user/user.reducer';
import {OrganizationsEffects} from './store/organizations/organizations.effects';
import {organizationsReducer} from './store/organizations/organizations.reducer';
import {metaReducers} from './store/app.state';
import { DownloadsComponent } from './pages/downloads/downloads.component';
import { BackupConfigRequestComponent } from './pages/secure-ssd/backup-config-request/backup-config-request.component';
import { TransferLicenseDialogComponent } from './pages/endpoints/transfer-license-dialog/transfer-license-dialog.component';
import { DynamicDashboardComponent } from './pages/dynamic-dashboard/dynamic-dashboard.component';
import { GridstackComponent, GridstackModule } from 'gridstack/dist/angular';
import { EndpointsConnectedComponent } from './pages/dynamic-dashboard/endpoints-connected/endpoints-connected.component';
import { EndpointsTotalComponent } from './pages/dynamic-dashboard/endpoints-total/endpoints-total.component';
import { EndpointsLockedComponent } from './pages/dynamic-dashboard/endpoints-locked/endpoints-locked.component';
import { ThreatsDetectedComponent } from './pages/dynamic-dashboard/threats-detected/threats-detected.component';
import { EndpointActivityComponent } from './pages/dynamic-dashboard/endpoint-activity/endpoint-activity.component';
import { EndpointLocationComponent } from './pages/dynamic-dashboard/endpoint-location/endpoint-location.component';
import { ThreatActivityOverviewComponent } from './pages/dynamic-dashboard/threat-activity-overview/threat-activity-overview.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { LetDirective } from '@ngrx/component';
import { DisplayRelativeChangesComponent } from './pages/dynamic-dashboard/display-relative-changes/display-relative-changes.component';
import { DisplayTotalValueComponent } from './pages/dynamic-dashboard/display-total-value/display-total-value.component';
import { TopThreatsComponent } from './pages/dynamic-dashboard/top-threats/top-threats.component';
import { EndpointsOverviewComponent } from './pages/dynamic-dashboard/endpoints-overview/endpoints-overview.component';
import { EndpointProtectionsComponent } from './pages/dynamic-dashboard/endpoint-protections/endpoint-protections.component';
import { EndpointTopThreatsComponent } from './pages/dynamic-dashboard/endpoint-top-threats/endpoint-top-threats.component';
import { DecryptionActivityComponent } from './pages/dynamic-dashboard/decryption-activity/decryption-activity.component';
import { ProvisioningTemplateComponent } from './dialogs/provisioning-template/provisioning-template.component';
import { BitlockerComponent } from './shared/components/bitlocker/bitlocker.component';
import { PolicyFileTypesComponent } from './shared/components/policy-file-types/policy-file-types.component';
import { BitlockerKeyRequestComponent } from './pages/d3e-device/bitlocker-key-request/bitlocker-key-request.component';
import { BitlockerDisplayKeyDialogComponent } from './pages/d3e-device/bitlocker-key-request/bitlocker-display-key-dialog/bitlocker-display-key-dialog.component';
import { BitlockerComplianceComponent } from './pages/dynamic-dashboard/bitlocker-compliance/bitlocker-compliance.component';
import { EndpointsAttentionComponent } from './pages/dynamic-dashboard/endpoints-attention/endpoints-attention.component';
import { CigentComplianceComponent } from './pages/dynamic-dashboard/cigent-compliance/cigent-compliance.component';
import { LicensingPostureComponent } from './pages/dynamic-dashboard/licensing-posture/licensing-posture.component';
import { D3eDeviceComplianceComponent } from './pages/d3e-device/d3e-device-compliance.component';
import { ThreatSensorsComponent } from './pages/d3e-device/threat-sensors/threat-sensors.component';
import { TitleStatusComponent } from './pages/d3e-device/threat-sensors/title-status/title-status.component';
import { DataProtectionsComponent } from './pages/d3e-device/data-protections/data-protections.component';
import { SearchResultsComponent } from './shared/components/search-all/search-results/search-results.component';
import { SearchAllComponent } from './shared/components/search-all/search-all.component';
import { SearchRecentComponent } from './shared/components/search-all/search-recent/search-recent.component';
import { ProductsComponent } from './pages/products/products.component';
import { DataDefenseSubComponent } from './pages/products/data-defense-sub/data-defense-sub.component';
import { HardwareSubComponent } from './pages/products/hardware-sub/hardware-sub.component';
import { BitlockerSubComponent } from './pages/products/bitlocker-sub/bitlocker-sub.component';
import { SubEndpointListComponent } from './pages/products/sub-endpoint-list/sub-endpoint-list.component';
import { SubscriptionHeadingComponent } from './pages/products/subscription-heading/subscription-heading.component';
import { AssignLicensesComponent } from './pages/assign-licenses/assign-licenses.component';
import { AddSsdSerialDialogComponent } from './pages/assign-licenses/add-ssd-serial-dialog/add-ssd-serial-dialog.component';
import { UploadOrEnterCodeDialogComponent } from './pages/assign-licenses/upload-or-enter-code-dialog/upload-or-enter-code-dialog.component';
import { HostsComponent } from './pages/hosts/hosts.component';
import { LicenseDetailsComponent } from './pages/assign-licenses/license-details/license-details.component';
import { TrackCapsDirective } from './shared/directives/caps-lock.directive'
import { PbaSettingsComponent } from './shared/components/pba-settings/pba-settings.component';
import { FdeSettingsComponent } from './shared/components/fde-settings/fde-settings.component';
import { PbaInfoComponent } from './shared/components/pba-info/pba-info.component';
import { FdeInfoComponent } from './shared/components/fde-info/fde-info.component';
import {PbaUsersComponent} from './shared/components/pba-users/pba-users.component';
import {ConfirmEndpointDialogComponent} from './shared/components/confirm-endpoint-dialog/confirm-endpoint-dialog.component';
import {EndpointUserDialogComponent} from './shared/components/endpoint-user-dialog/endpoint-user-dialog.component';
import {ManageEndpointUserDialogComponent} from './shared/components/endpoint-user-dialog/manage-endpoint-user-dialog.component';
import {EnrollCodesDialogComponent} from './shared/components/enroll-codes-dialog/enroll-codes-dialog.component';
import {EnrollCodesAddedDialogComponent} from './shared/components/enroll-codes-dialog/enroll-codes-added-dialog.component';
import { PbaPasswordRequirementsComponent } from './shared/components/pba-password-requirements/pba-password-requirements.component';
import {CtmcErrorHandler} from '../shared/ctmc-error-handler';
import { GenerateConfirmationDialogComponent } from './pages/assign-licenses/generate-confirmation-dialog/generate-confirmation-dialog.component';
import { OktaComponent } from './pages/integrations/okta/okta.component';
import { LoginOktaComponent } from './pages/login-okta/login-okta.component';
import { OktaCallbackComponent } from './pages/okta-callback/okta-callback.component';
import { TourMatMenuModule } from 'ngx-ui-tour-md-menu';
import { UserTourComponent } from './shared/components/user-tour/user-tour.component';
import { BatchUpdateCustomDialogComponent } from './pages/assign-licenses/batch-update-custom-dialog/batch-update-custom-dialog.component';
import { UpdateRequestComponent } from './pages/update-request/update-request.component';
import {InfiniteScrollDirective} from 'ngx-infinite-scroll';
import { FdeSubComponent } from './pages/products/fde-sub/fde-sub.component';
import { AssignFdeLicensesComponent } from './pages/assign-fde-licenses/assign-fde-licenses.component';
import { FdeLicenseDetailsComponent } from './pages/assign-fde-licenses/fde-license-details/fde-license-details.component';
import { AddWorkstationDialogComponent } from './pages/assign-fde-licenses/add-workstation-dialog/add-workstation-dialog.component';
import { EnterFdeRequestDialogComponent } from './pages/assign-fde-licenses/enter-fde-request-dialog/enter-fde-request-dialog.component';
import { SecurityRequestsStatusListDialogComponent } from './shared/components/security-requests-status-list-dialog/security-requests-status-list-dialog.component';
import { PolicyComplianceStatusComponent } from './pages/d3e-device/policy-compliance-status/policy-compliance-status.component';
import { GenerateFdeLicenseDialogComponent } from './pages/assign-fde-licenses/generate-fde-license-dialog/generate-fde-license-dialog.component';
import { AgentSubComponent } from './pages/products/agent-sub/agent-sub.component';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import { LeafletMarkerClusterModule } from '@bluehalo/ngx-leaflet-markercluster';
import { AgentDownloadComponent } from './pages/agent-download/agent-download.component';


// AoT requires an exported function for factories
export function createTranslateLoader(http: HttpClient) {
    return new TranslateHttpLoader(http, '../assets/i18n/', '.json');
}

export function getToken(): string {
    return localStorage.getItem('token');
}

export function appInitializerFactory(translate: TranslateService) {
    return async () => {
        const languageCodes: string[] = supportedLanguages.map(n => n.code);
        // zh and zh-CN are simplified Chinese, we want to support both language codes,
        // but don't want to duplicate language files, so replace zh-CN with zh in localStorage
        // and the translateService.
        translate.addLangs(languageCodes.filter(c => c !== 'zh-CN'));
        let defaultLang: string = languageCodes.find(lang => lang === window.navigator.language) || 'en';
        defaultLang = defaultLang.replace('zh-CN', 'zh');
        defaultLang = defaultLang.replace('pt-BR', 'pt');

        localStorage.setItem('language', defaultLang);

        translate.setDefaultLang(defaultLang);
        translate.use(defaultLang);
        return new Promise<void>(resolve => {
            translate.onLangChange.subscribe(() => {
                resolve();
            });
        });
    };
}

@NgModule({ declarations: [
        AppComponent,
        routingComponents,
        GoogleAuthDialogComponent,
        AutofocusDirective,
        ResetPasswordComponent,
        EditSubscriptionNameComponent,
        NumericInputComponent,
        EndpointsComponent,
        GlobalD3ePolicyComponent,
        D3eDeviceComponent,
        IntegrationsComponent,
        CarbonBlackComponent,
        CyberarkComponent,
        HistoryComponent,
        ThirdPartyComponent,
        DefaultFileFormatComponent,
        CustomFileFormatComponent,
        PolicyFoldersComponent,
        PolicyEditorComponent,
        PolicySettingsComponent,
        PolicyListComponent,
        ActiveLockConfigurationComponent,
        ProtectionLevelBottomSheetComponent,
        SophosComponent,
        DebounceClickDirective,
        ThrottleClickDirective,
        DebounceInputDirective,
        TopNDevicesComponent,
        TopNThreatsComponent,
        DeviceOverviewComponent,
        DeviceConfigComponent,
        ThreatActivityComponent,
        UserSettingsComponent,
        CountryCodeComponent,
        PolicyKnownGoodComponent,
        PathListComponent,
        UploadDialogComponent,
        CiscoAmpComponent,
        CiscoDuoComponent,
        AdminComponent,
        UserGroupRoleComponent,
        EventViewerComponent,
        PolicySafeAppComponent,
        AddOrganizationComponent,
        AddChildSubscriptionComponent,
        EditOrgComponent,
        EditLicenseCountComponent,
        EditExpDateComponent,
        EditUserDialogComponent,
        AlertDialogComponent,
        ActionConfirmationDialogComponent,
        IntegrationCardComponent,
        D3eDeviceComplianceComponent,
        DataDeceptionFilesComponent,
        FileDecryptionComponent,
        DndDropFileDirective,
        ProgressComponent,
        ManageKeysComponent,
        D3eDownloadComponent,
        InvitationComponent,
        ManageUsersComponent,
        AddPeerUserDialogComponent,
        EditPeerUserDialogComponent,
        ReplaceSpacePipe,
        PasswordComponent,
        NewsComponent,
        ImportExtsFoldersComponent,
        LifecycleTrackerDirective,
        D3eVersionHistoryComponent,
        EndpointsComponent,
        GroupsComponent,
        AddGroupDialogComponent,
        EditGroupDialogComponent,
        AssignGroupDialogComponent,
        RedirectorComponent,
        DownloadedFreeComponent,
        LogoCardPageComponent,
        SaveKeyDialogComponent,
        SecureSsdComponent,
        ColumnChooserComponent,
        CustomSnackBarComponent,
        AdminSettingsComponent,
        FileDecryptComponent,
        DiskSizePipe,
        D3eInvitationComponent,
        WelcomeComponent,
        HeaderInfoComponent,
        GetPasswordComponent,
        MultiLineOptionDirective,
        LanguageSelectorComponent,
        ActiveDirectoryComponent,
        OktaComponent,
        LoginMsComponent,
        VerifyEmailComponent,
        EmailVerifyComponent,
        ReparentDialogComponent,
        DownloadCigentComponent,
        PlusTrialComponent,
        PasswordRulesComponent,
        EditLicenseDurationComponent,
        AlertClipboardCopyComponent,
        SecurityApprovalBannerComponent,
        SecurityRequestStatusDialogComponent,
        RecoveryKeyRequestComponent,
        SecurityRequestDialogComponent,
        CreateAdminEventComponent,
        ButtonFocusRemoverDirective,
        AzureActivateComponent,
        CigentHeaderComponent,
        CigentCapabilitiesComponent,
        CigentFooterComponent,
        IndirectLoginSuccessComponent,
        ReParentUserDialogComponent,
        SentinelOneComponent,
        DeleteOrganizationDialogComponent,
        PriorityLockBannerComponent,
        EncryptionEventViewerComponent,
        ExpiresInPipe,
        MaintenanceComponent,
        CryptographyComponent,
        UploadKeyDialogComponent,
        KeyStatusTextPipe,
        ExportKeyDialogComponent,
        ExportD3eKeysDialogComponent,
        EditKeyDialogComponent,
        PasswordAlertDialogComponentComponent,
        IntegrationTestingComponent,
        RansomwareFileTypesComponent,
        GroupDetailsComponent,
        GroupDetailsDeviceComponent,
        GroupDetailsUserComponent,
        OrgsOverviewComponent,
        OrgsOverviewOrgComponent,
        OrgsOverviewUserComponent,
        LockEndpointsDialogComponent,
        DownloadsComponent,
        BackupConfigRequestComponent,
        TransferLicenseDialogComponent,
        DynamicDashboardComponent,
        EndpointsConnectedComponent,
        EndpointsTotalComponent,
        EndpointsLockedComponent,
        ThreatsDetectedComponent,
        DisplayTotalValueComponent,
        EndpointActivityComponent,
        EndpointLocationComponent,
        ThreatActivityOverviewComponent,
        DisplayRelativeChangesComponent,
        TopThreatsComponent,
        EndpointsOverviewComponent,
        EndpointProtectionsComponent,
        EndpointTopThreatsComponent,
        DecryptionActivityComponent,
        ProvisioningTemplateComponent,
        BitlockerComponent,
        PolicyFileTypesComponent,
        BitlockerKeyRequestComponent,
        BitlockerDisplayKeyDialogComponent,
        BitlockerComplianceComponent,
        EndpointsAttentionComponent,
        LicensingPostureComponent,
        CigentComplianceComponent,
        ThreatSensorsComponent,
        TitleStatusComponent,
        DataProtectionsComponent,
        SearchResultsComponent,
        SearchAllComponent,
        SearchRecentComponent,
        ProductsComponent,
        DataDefenseSubComponent,
        HardwareSubComponent,
        BitlockerSubComponent,
        SubEndpointListComponent,
        SubscriptionHeadingComponent,
        AssignLicensesComponent,
        AddSsdSerialDialogComponent,
        UploadOrEnterCodeDialogComponent,
        HostsComponent,
        LicenseDetailsComponent,
        PbaSettingsComponent,
        FdeSettingsComponent,
        FdeInfoComponent,
        PbaInfoComponent,
        PbaUsersComponent,
        ConfirmEndpointDialogComponent,
        EnrollCodesDialogComponent,
        EnrollCodesAddedDialogComponent,
        EndpointUserDialogComponent,
        ManageEndpointUserDialogComponent,
        PbaPasswordRequirementsComponent,
        GenerateConfirmationDialogComponent,
        OktaComponent,
        LoginOktaComponent,
        OktaCallbackComponent,
        UserTourComponent,
        BatchUpdateCustomDialogComponent,
        UpdateRequestComponent,
        FdeSubComponent,
        AssignFdeLicensesComponent,
        FdeLicenseDetailsComponent,
        AddWorkstationDialogComponent,
        EnterFdeRequestDialogComponent,
        SecurityRequestsStatusListDialogComponent,
        PolicyComplianceStatusComponent,
        GenerateFdeLicenseDialogComponent,
        AgentSubComponent,
        AgentDownloadComponent,
    ],
    exports: [
        MatLineModule,
        MultiLineOptionDirective
    ],
    bootstrap: [AppComponent],
    imports: [
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: createTranslateLoader,
                deps: [HttpClient]
            }
        }),
        AngularDualListBoxModule,
        CommonModule,
        BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        FormsModule,
        ReactiveFormsModule,
        BrowserAnimationsModule,
        MatCheckboxModule,
        MatCheckboxModule,
        MatBottomSheetModule,
        MatButtonModule,
        MatInputModule,
        MatAutocompleteModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatFormFieldModule,
        MatRadioModule,
        MatSelectModule,
        MatSliderModule,
        MatSlideToggleModule,
        MatMenuModule,
        MatSidenavModule,
        MatToolbarModule,
        MatListModule,
        MatGridListModule,
        MatCardModule,
        MatStepperModule,
        MatTabsModule,
        MatExpansionModule,
        MatButtonToggleModule,
        MatChipsModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatProgressBarModule,
        MatDialogModule,
        MatTooltipModule,
        MatSnackBarModule,
        MatTableModule,
        MatSortModule,
        MatPaginatorModule,
        LayoutModule,
        NgxFilesizeModule,
        DragDropModule,
        NgxChartsModule,
        MatLineModule,
        MatRippleModule,
        GridstackModule,
        TrackCapsDirective,
        TourMatMenuModule,
        NgxSkeletonLoaderModule.forRoot({
            theme: {
                // Enabling theme combination
                extendsFromRoot: true,
                // ... list of CSS theme attributes
                'background-color': 'rgba(0,0,0,0.07)',
            },
        }),
        LeafletModule,
        JwtModule.forRoot({
            config: {
                tokenGetter: getToken
            }
        }),
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: environment.enableServiceWorker,
            registrationStrategy: 'registerImmediately'
        }),
        StoreModule.forRoot({
            user: userReducer,
            organizations: organizationsReducer,
        }, { metaReducers }),
        EffectsModule.forRoot([
            UserEffects,
            OrganizationsEffects
        ]),
        LetDirective,
        LeafletMarkerClusterModule,
        InfiniteScrollDirective],
    providers: [
        TranslateService,
        AuthService,
        JwtHelperService,
        UserService,
        NotificationService,
        D3eService,
        ActionService,
        PageDeactivateService,
        ClientPolicyService,
        ThemeService,
        ConfigurationService,
        WINDOW_PROVIDERS,
        provideAppInitializer(() => {
        const initializerFn = (configurationServiceFactory)(inject(ConfigurationService));
        return initializerFn();
      }),
        provideAppInitializer(() => {
        const initializerFn = (appInitializerFactory)(inject(TranslateService));
        return initializerFn();
      }),
        { provide: MAT_BOTTOM_SHEET_DEFAULT_OPTIONS, useValue: { hasBackdrop: false } },
        { provide: HTTP_INTERCEPTORS, useClass: HttpsRequestInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
        { provide: ErrorHandler, useClass: CtmcErrorHandler, deps: [ConfigurationService] },
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule {
  constructor() {
    // register all our dynamic components types created by the grid
    GridstackComponent.addComponentToSelectorType([
      EndpointsConnectedComponent,
      EndpointsTotalComponent,
      EndpointsLockedComponent,
      ThreatsDetectedComponent,
      EndpointLocationComponent,
      EndpointActivityComponent,
      ThreatActivityOverviewComponent,
      TopThreatsComponent,
      EndpointsOverviewComponent,
      EndpointProtectionsComponent,
      EndpointTopThreatsComponent,
      DecryptionActivityComponent,
      BitlockerComplianceComponent,
      EndpointsAttentionComponent,
      LicensingPostureComponent,
      CigentComplianceComponent
    ]);
  }
}
